// components/ProductSection.jsx
import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import ProductCard from "./ProductCard";
import { Link } from "react-router-dom";
import { useCurrency } from "../context/CurrencyContext";
import { isAvailableIn } from "../utils/pricing";

const ProductSection = () => {
    const { currency, fromPrice, formatPrice } = useCurrency();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "products"));
                const productList = [];

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data.isActive === false) return;
                    productList.push({
                        id: doc.id,
                        name: data.name || "Unnamed Product",
                        price: data.price || 0,
                        price_cents: data.price ? data.price * 100 : 0,
                        image: data.images?.[0] || data.image || "https://placehold.co/500x500",
                        images: data.images || [],
                        description: data.description || "",
                        soldOut: data.soldOut || false,
                        stock: data.stock || 0,
                        category: data.category || "Uncategorized",
                        variants: data.variants || [],
                        colors: data.colors || [],
                        pricingMatrix: data.pricingMatrix || {},
                        pricingMatrixUSD: data.pricingMatrixUSD || {},
                        priceUSD: data.priceUSD ?? null,
                        optionColors: data.optionColors || [],
                        optionLengths: data.optionLengths || [],
                        optionTextures: data.optionTextures || [],
                        optionTypes: data.optionTypes || [],
                        colorImages: data.colorImages || {}
                    });
                });

                setProducts(productList);
            } catch (err) {
                console.error("Error fetching products:", err);
                setError("Failed to load products. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    // US visitors only see products that have at least one $ price.
    // Each product gets the "from" price in the visitor's currency for ProductCard:
    //   product.displayPrice → "£120" / "$165"   product.fromPrice → 120 / 165
    const visibleProducts = products
        .filter(product => currency === "GBP" || isAvailableIn(product, "USD"))
        .map(product => {
            const from = fromPrice(product);
            return { ...product, currency, fromPrice: from, displayPrice: formatPrice(from) };
        });

    // Filter products by category
    const hairBundles = visibleProducts.filter(product =>
        product.category === "Hair Bundles" || product.category === "Uncategorized"
    );

    const frontalsAndClosures = visibleProducts.filter(product =>
        product.category === "Frontals and Closures"
    );

    const colouredHair = visibleProducts.filter(product =>
        product.category === "Coloured Hair"
    );

    // Custom smooth scroll that accounts for your sticky navbar!
    const scrollToCategory = (categoryId) => {
        const element = document.getElementById(categoryId);
        if (element) {
            // Subtract 100px so the sticky header doesn't cover the title
            const y = element.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    if (loading) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-primary" style={{ color: "#d4af37" }} role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2" style={{ color: "rgba(245,240,232,0.5)", letterSpacing: "2px", textTransform: "uppercase", fontSize: "0.8rem" }}>Loading Collection...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5 text-center">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <section id="shop" className="container py-5">

            {/* ── NEW: Quick Navigation Bar ── */}
            <div style={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "15px",
                marginBottom: "50px",
                paddingBottom: "30px",
                borderBottom: "1px solid rgba(212, 175, 55, 0.15)"
            }}>
                {hairBundles.length > 0 && (
                    <button onClick={() => scrollToCategory('category-bundles')} className="btn-luxury-outline" style={{ padding: "10px 20px", fontSize: "0.65rem" }}>
                        Hair Bundles
                    </button>
                )}
                {frontalsAndClosures.length > 0 && (
                    <button onClick={() => scrollToCategory('category-frontals')} className="btn-luxury-outline" style={{ padding: "10px 20px", fontSize: "0.65rem" }}>
                        Frontals & Closures
                    </button>
                )}
                {colouredHair.length > 0 && (
                    <button onClick={() => scrollToCategory('category-coloured')} className="btn-luxury-outline" style={{ padding: "10px 20px", fontSize: "0.65rem" }}>
                        Coloured Hair
                    </button>
                )}
            </div>

            {/* Hair Bundles Section */}
            <h2 id="category-bundles" className="section-title">HAIR BUNDLES</h2>
            <div className="row">
                {hairBundles.length > 0 ? (
                    hairBundles.map((product) => (
                        <div key={product.id} className="col-6 col-md-4 mb-4">
                            <Link to={`/product/${product.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                                <ProductCard product={product} />
                            </Link>
                        </div>
                    ))
                ) : (
                    <div className="col-12 text-center"><p className="text-muted">No hair bundles available right now.</p></div>
                )}
            </div>

            {/* Frontals and Closures Section */}
            <h2 id="category-frontals" className="section-title mt-5">FRONTALS AND CLOSURES</h2>
            <div className="row">
                {frontalsAndClosures.length > 0 ? (
                    frontalsAndClosures.map((product) => (
                        <div key={product.id} className="col-6 col-md-4 mb-4">
                            <Link to={`/product/${product.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                                <ProductCard product={product} />
                            </Link>
                        </div>
                    ))
                ) : (
                    <div className="col-12 text-center"><p className="text-muted">No frontals and closures available right now.</p></div>
                )}
            </div>

            {/* Coloured Hair Section */}
            <h2 id="category-coloured" className="section-title mt-5">COLOURED HAIR</h2>
            <div className="row">
                {colouredHair.length > 0 ? (
                    colouredHair.map((product) => (
                        <div key={product.id} className="col-6 col-md-4 mb-4">
                            <Link to={`/product/${product.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                                <ProductCard product={product} />
                            </Link>
                        </div>
                    ))
                ) : (
                    <div className="col-12 text-center"><p className="text-muted">No coloured hair products available right now.</p></div>
                )}
            </div>
        </section>
    );
};

export default ProductSection;